import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  TextField,
  FormControlLabel,
  Chip,
  Badge,
  Stack,
  Alert,
  CircularProgress,
  Tooltip,
  Tabs,
  Tab,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useAppDispatch, useAppSelector } from 'src/store';
import {
  fetchChunks,
  patchChunk,
  deleteChunk,
  createChunk,
  createRuleViaNotesApi,
  updateChunk,
  selectAllChunks,
  selectChunksLoading,
  selectChunksError,
  selectChunksByStatus,
  setFilters,
  Chunk,
} from 'src/features/chunks/chunks.slice';
import { format } from 'date-fns';

interface ChunksManagementDialogProps {
  open: boolean;
  onClose: () => void;
}

interface ChunkFormData {
  use_when: string;
  content: string;
  example: string;
  enabled: boolean;
}

interface ParsedChunkContent {
  conversation_summary?: string;
  key_points?: string[];
  user_sentiment?: string;
  assistant_performance_score?: number;
  situation?: string;
  rule?: string;
  example_string?: string;
  content?: string; // fallback for simple content
}

// Utility function to safely parse chunk content
const parseChunkContent = (content: any): ParsedChunkContent => {
  try {
    if (typeof content === 'object' && content !== null) {
      return content; // Already an object (from feedback chunks)
    } else if (typeof content === 'string') {
      // Handle both JSON format and Ruby hash string format
      let contentStr = content;
      // Convert Ruby hash syntax to JSON if needed
      if (contentStr.includes('=>')) {
        contentStr = contentStr.replace(/=>/g, ':');
      }
      return JSON.parse(contentStr); // Parse JSON string (from manual rules)
    } else {
      return { content: content };
    }
  } catch (error) {
    console.error('Failed to parse chunk content:', content, error);
    return { content: content };
  }
};

const ChunksManagementDialog: React.FC<ChunksManagementDialogProps> = ({ open, onClose }) => {
  const dispatch = useAppDispatch();
  const chunks = useAppSelector(selectAllChunks);
  const loading = useAppSelector(selectChunksLoading);
  const error = useAppSelector(selectChunksError);
  const pendingChunks = useAppSelector(state => selectChunksByStatus(state, 'pending_admin_approval'));
  
  const [selectedTab, setSelectedTab] = useState(0);
  const [viewMode, setViewMode] = useState<'list' | 'form' | 'review'>('list');
  const [selectedChunk, setSelectedChunk] = useState<Chunk | null>(null);
  const [formData, setFormData] = useState<ChunkFormData>({
    use_when: '',
    content: '',
    example: '',
    enabled: true,
  });
  const [searchQuery, setSearchQuery] = useState('');
  // add state for review form data
  const [reviewFormData, setReviewFormData] = useState<ChunkFormData>({
    use_when: '',
    content: '',
    example: '',
    enabled: true,
  });
  const [isEditingField, setIsEditingField] = useState<'use_when' | 'content' | 'example' | null>(null);
  const [processingAction, setProcessingAction] = useState<'accept' | 'reject' | null>(null);

  useEffect(() => {
    if (open) {
      // fetch rule chunks when dialog opens
      dispatch(fetchChunks({ chunk_type: 'rule', per_page: 30 }));
    }
  }, [open, dispatch]);

  // add a useEffect to debug chunks data
  // useEffect(() => {
  //   console.log('Current chunks:', chunks);
  //   console.log('Pending chunks:', pendingChunks);
  // }, [chunks, pendingChunks]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
    setViewMode('list');
  };

  const handleAddNew = () => {
    setFormData({
      use_when: '',
      content: '',
      example: '',
      enabled: true,
    });
    setSelectedChunk(null);
    setViewMode('form');
  };

  const handleEdit = (chunk: Chunk) => {
    // Use the reusable parsing function
    const parsedContent = parseChunkContent(chunk.content);
    // console.log('Parsed content in handleEdit:', parsedContent);

    setFormData({
      use_when: chunk.metadata?.use_when || parsedContent.situation || '',
      content: parsedContent.rule || parsedContent.content || chunk.content,
      example: parsedContent.example_string || parsedContent.content || chunk.content,
      enabled: chunk.status === 'active',
    });
    setSelectedChunk(chunk);
    setViewMode('form');
  };

  const handleDelete = async (chunk: Chunk) => {
    const targetId = (chunk as any).originalId || chunk.id;
    if (window.confirm('Are you sure you want to delete this rule?')) {
      await dispatch(deleteChunk(targetId));
    }
  };

  const handleSave = async () => {
    if (!formData.use_when || !formData.content || !formData.example) {
      alert('Please fill in all fields');
      return;
    }

    if (selectedChunk) {
      // update existing chunk using the existing chunk API
      const chunkData = {
        content: JSON.stringify({
          rule: formData.content,
          situation: formData.use_when,
          content: formData.content,
          example_string: formData.example,
        }),
        chunk_type: 'rule',
        status: formData.enabled ? 'active' as const : 'pending_admin_approval' as const,
        metadata: {
          use_when: formData.use_when
        },
      };

      const targetId = (selectedChunk as any).originalId || selectedChunk.id;
      await dispatch(patchChunk({
        id: targetId,
        ...chunkData,
      }));
    } else {
      // create new rule using the Notes API
      const ruleData = {
        situation: formData.use_when,
        rule: formData.content,
        example: formData.example,
        rule_type: 'assistant_behavior',
        status: formData.enabled ? 'active' : 'pending_admin_approval',
      };

      await dispatch(createRuleViaNotesApi(ruleData));
    }

    setViewMode('list');
    dispatch(fetchChunks({ chunk_type: 'rule', per_page: 30 }));
  };

  const handleReview = (chunk: Chunk) => {
    // console.log('handleReview called with chunk:', chunk);
    // Use the reusable parsing function
    const parsedContent = parseChunkContent(chunk.content);
    // console.log('Original parsed content in review:', parsedContent);

    setReviewFormData({
      use_when: chunk.metadata?.use_when || parsedContent.situation || '',
      content: parsedContent.rule || parsedContent.content || chunk.content,
      example: parsedContent.example_string || parsedContent.content || chunk.content,
      enabled: chunk.status === 'active',
    });
    setSelectedChunk(chunk);
    setViewMode('review');
  };

  const handleAccept = async () => {
    if (selectedChunk) {
      setProcessingAction('accept');
      try {
        // Parse original content to preserve example_string if available
        const originalParsedContent = parseChunkContent(selectedChunk.content);

        // update chunk with edited content and set status to active
        const contentObj = {
          rule: reviewFormData.content,
          situation: reviewFormData.use_when,
          content: reviewFormData.content,
          example_string: reviewFormData.example,
        };
        // console.log('Sending content object:', contentObj);
        
        const chunkData = {
          content: JSON.stringify(contentObj),
          chunk_type: 'rule',
          status: 'active' as const,
          metadata: {
            use_when: reviewFormData.use_when,
            enabled: true,
          },
        };

        const targetId = (selectedChunk as any).originalId || selectedChunk.id;
        await dispatch(
          patchChunk({ id: targetId, ...chunkData })
        );
        setViewMode('list');
        setSelectedTab(0); // switch to All Rules tab
        dispatch(fetchChunks({ chunk_type: 'rule', per_page: 30 }));
      } finally {
        setProcessingAction(null);
      }
    }
  };

  const handleReject = async () => {
    if (selectedChunk) {
      setProcessingAction('reject');
      try {
        // delete the chunk instead of marking as invalid
        const targetId = (selectedChunk as any).originalId || selectedChunk.id;
        await dispatch(deleteChunk(targetId));
        setViewMode('list');
        dispatch(fetchChunks({ chunk_type: 'rule', status: 'pending_admin_approval', per_page: 30 }));
      } finally {
        setProcessingAction(null);
      }
    }
  };

  const handleToggleStatus = async (chunk: Chunk) => {
    const newStatus = chunk.status === 'active' ? 'invalid' : 'active';
    const targetId = (chunk as any).originalId || chunk.id;
    await dispatch(patchChunk({
      id: targetId,
      status: newStatus,
    }));
    // reload the current view
    if (selectedTab === 0) {
      dispatch(fetchChunks({ chunk_type: 'rule', per_page: 30 }));
    } else {
      dispatch(fetchChunks({ chunk_type: 'rule', status: 'pending_admin_approval', per_page: 30 }));
    }
  };

  const filteredChunks = chunks.filter(chunk => {
    // First filter by tab selection
    if (selectedTab === 0) {
      // "All rules" tab - show all chunks including pending ones
      if (chunk.status === 'pending_admin_approval') return true;
    } else if (selectedTab === 1) {
      // "Pending" tab - show only pending chunks
      if (chunk.status !== 'pending_admin_approval') return false;
    }
    
    // Then filter by search query
    if (!searchQuery) return true;
    const searchLower = searchQuery.toLowerCase();
    return chunk.content.toLowerCase().includes(searchLower) ||
           chunk.metadata?.use_when?.toLowerCase().includes(searchLower);
  });

  const renderListView = () => (
    <>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <TextField
          placeholder="Search rules..."
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ width: 300 }}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddNew}
          disabled={loading}
          sx={{
            bgcolor: '#000',
            color: '#fff',
            '&:hover': { bgcolor: '#333' },
            '&:disabled': { bgcolor: '#666', color: '#999' },
            borderRadius: '20px',
            textTransform: 'none',
          }}
        >
          Add new rule
        </Button>
      </Box>

      {loading && <CircularProgress />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper} sx={{ bgcolor: 'background.paper' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Situation</TableCell>
              <TableCell>Content</TableCell>
              <TableCell>Created at</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredChunks.map((chunk) => {
              console.log('Processing chunk:', chunk.id);
              // Use the reusable parsing function
              const parsedContent = parseChunkContent(chunk.content);
              console.log('Chunk parsed!');

              return (
                <TableRow key={chunk.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {chunk.metadata?.use_when || parsedContent.situation || 'Unnamed situation'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ 
                      maxWidth: 400, 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap' 
                    }}>
                      {parsedContent.rule || parsedContent.content || parsedContent.example_string || parsedContent.conversation_summary || chunk.content}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {format(new Date(chunk.created_at), 'MM/dd/yyyy HH:mm')}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {chunk.status === 'pending_admin_approval' ? (
                      <Chip 
                        label="Pending" 
                        size="small" 
                        color="warning"
                        onClick={() => handleReview(chunk)}
                        sx={{ cursor: 'pointer' }}
                      />
                    ) : (
                      <Switch
                        size="small"
                        checked={chunk.status === 'active'}
                        onChange={() => handleToggleStatus(chunk)}
                        disabled={loading}
                        color="primary"
                      />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={1} justifyContent="center">
                      {chunk.status === 'pending_admin_approval' ? (
                        <Tooltip title="Review">
                          <IconButton 
                            size="small" 
                            onClick={() => handleReview(chunk)}
                            disabled={loading}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Edit">
                          <IconButton 
                            size="small" 
                            onClick={() => handleEdit(chunk)}
                            disabled={loading}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title={loading ? "Processing..." : "Delete"}>
                        <IconButton 
                          size="small" 
                          onClick={() => handleDelete(chunk)}
                          disabled={loading}
                        >
                          {loading ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <DeleteIcon fontSize="small" color="error" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );

  const renderFormView = () => (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton 
          onClick={() => setViewMode('list')} 
          disabled={loading}
          sx={{ mr: 2 }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6">
          {selectedChunk ? 'Edit rule' : 'Add new rule'}
        </Typography>
      </Box>

      <Typography variant="subtitle1" sx={{ mb: 3, color: 'text.secondary' }}>
        Rules are how your Oppy Assistant learns your unique style. Every rule defines a pattern and a response.
      </Typography>

      <Stack spacing={3}>
        <TextField
          label="Situation (When to use this rule)"
          fullWidth
          multiline
          rows={2}
          value={formData.use_when}
          onChange={(e) => setFormData({ ...formData, use_when: e.target.value })}
          placeholder="e.g., When a buyer inquires about a property but hasn't provided pre-approval"
        />

        <TextField
          label="Rule Description"
          fullWidth
          multiline
          rows={3}
          value={formData.content}
          onChange={(e) => setFormData({ ...formData, content: e.target.value })}
          placeholder="e.g., Politely request pre-approval or proof of funds before proceeding with property viewings"
        />

        <TextField
          label="Example Response"
          fullWidth
          multiline
          rows={3}
          value={formData.example}
          onChange={(e) => setFormData({ ...formData, example: e.target.value })}
          placeholder="e.g., Thank you for your interest! To ensure we can provide the best service, I'll need to see your pre-approval letter or proof of funds before we schedule a viewing."
        />

        <FormControlLabel
          control={
            <Switch
              checked={formData.enabled}
              onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
            />
          }
          label="Enabled"
        />
      </Stack>

      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button variant="text" onClick={() => setViewMode('list')} disabled={loading}>
          Cancel
        </Button>
        <Button 
          variant="contained" 
          onClick={handleSave}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
          sx={{
            bgcolor: '#000',
            color: '#fff',
            '&:hover': { bgcolor: '#333' },
            '&:disabled': { bgcolor: '#666', color: '#999' },
          }}
        >
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </Box>
    </Box>
  );

  const renderReviewView = () => {
    if (!selectedChunk) return null;

    return (
      <Box sx={{ p: 2 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" sx={{ mb: 1 }}>
            Customize this rule
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            This rule was generated based on your action. Review it below, you can fine-tune the trigger and the expected response.
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
            💡 Click on any text field below to edit it
          </Typography>
        </Box>

        <Paper 
          sx={{ 
            p: 3, 
            mb: 3,
            border: '2px solid',
            borderColor: 'warning.main',
            borderRadius: 2,
            bgcolor: 'background.paper',
          }}
        >
          <Chip 
            label="Pending" 
            color="warning" 
            size="small" 
            sx={{ mb: 3 }} 
          />
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Use when
            </Typography>
            <Box
              onClick={() => isEditingField !== 'use_when' && setIsEditingField('use_when')}
              sx={{ 
                p: 1.5,
                bgcolor: 'background.default',
                borderRadius: 1,
                cursor: isEditingField !== 'use_when' ? 'pointer' : 'default',
                minHeight: '50px',
                '&:hover': isEditingField !== 'use_when' ? { 
                  bgcolor: 'action.hover',
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                  outlineOffset: '-2px',
                } : {}
              }}
            >
              {isEditingField === 'use_when' ? (
                <TextField
                  fullWidth
                  multiline
                  autoFocus
                  value={reviewFormData.use_when}
                  onChange={(e) => setReviewFormData({ ...reviewFormData, use_when: e.target.value })}
                  onBlur={() => setIsEditingField(null)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      setIsEditingField(null);
                    }
                  }}
                  variant="standard"
                  InputProps={{
                    disableUnderline: true,
                    sx: { fontSize: '0.875rem' }
                  }}
                  sx={{ margin: 0 }}
                />
              ) : (
                <Typography variant="body2" color={reviewFormData.use_when ? 'text.primary' : 'text.secondary'}>
                  {reviewFormData.use_when || 'Click to add condition...'}
                </Typography>
              )}
            </Box>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Content
            </Typography>
            <Box
              onClick={() => isEditingField !== 'content' && setIsEditingField('content')}
              sx={{ 
                p: 1.5,
                bgcolor: 'background.default',
                borderRadius: 1,
                cursor: isEditingField !== 'content' ? 'pointer' : 'default',
                minHeight: '100px',
                '&:hover': isEditingField !== 'content' ? { 
                  bgcolor: 'action.hover',
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                  outlineOffset: '-2px',
                } : {}
              }}
            >
              {isEditingField === 'content' ? (
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  autoFocus
                  value={reviewFormData.content}
                  onChange={(e) => setReviewFormData({ ...reviewFormData, content: e.target.value })}
                  onBlur={() => setIsEditingField(null)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      setIsEditingField(null);
                    }
                  }}
                  variant="standard"
                  InputProps={{
                    disableUnderline: true,
                    sx: { fontSize: '0.875rem' }
                  }}
                  sx={{ margin: 0 }}
                />
              ) : (
                <Typography variant="body2" color={reviewFormData.content ? 'text.primary' : 'text.secondary'}>
                  {reviewFormData.content || 'Click to add content...'}
                </Typography>
              )}
            </Box>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Example Response
            </Typography>
            <Box
              onClick={() => isEditingField !== 'example' && setIsEditingField('example')}
              sx={{ 
                p: 1.5,
                bgcolor: 'background.default',
                borderRadius: 1,
                cursor: isEditingField !== 'example' ? 'pointer' : 'default',
                minHeight: '100px',
                '&:hover': isEditingField !== 'example' ? { 
                  bgcolor: 'action.hover',
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                  outlineOffset: '-2px',
                } : {}
              }}
            >
              {isEditingField === 'example' ? (
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  autoFocus
                  value={reviewFormData.example}
                  onChange={(e) => setReviewFormData({ ...reviewFormData, example: e.target.value })}
                  onBlur={() => setIsEditingField(null)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      setIsEditingField(null);
                    }
                  }}
                  variant="standard"
                  InputProps={{
                    disableUnderline: true,
                    sx: { fontSize: '0.875rem' }
                  }}
                  sx={{ margin: 0 }}
                />
              ) : (
                <Typography variant="body2" color={reviewFormData.example ? 'text.primary' : 'text.secondary'}>
                  {reviewFormData.example || 'Click to add example response...'}
                </Typography>
              )}
            </Box>
          </Box>
        </Paper>

        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button 
            variant="outlined" 
            startIcon={processingAction === 'reject' ? <CircularProgress size={20} color="inherit" /> : <ClearIcon />}
            onClick={handleReject}
            disabled={processingAction !== null}
            color="error"
            sx={{ 
              textTransform: 'none',
              opacity: processingAction === 'accept' ? 0.5 : 1,
            }}
          >
            {processingAction === 'reject' ? 'Rejecting...' : 'Reject'}
          </Button>
          <Button 
            variant="contained" 
            startIcon={processingAction === 'accept' ? <CircularProgress size={20} color="inherit" /> : <CheckIcon />}
            onClick={handleAccept}
            disabled={processingAction !== null}
            color="success"
            sx={{ 
              textTransform: 'none',
              opacity: processingAction === 'reject' ? 0.5 : 1,
            }}
          >
            {processingAction === 'accept' ? 'Accepting...' : 'Accept'}
          </Button>
        </Box>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '600px' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h5" fontWeight={600}>
            {viewMode === 'list' ? 'Rules' : viewMode === 'form' ? (selectedChunk ? 'Edit rule' : 'Add new rule') : 'Suggested rule'}
          </Typography>
          {viewMode === 'review' && (
            <Button
              size="small"
              startIcon={<ArrowBackIcon />}
              onClick={() => setViewMode('list')}
              sx={{ 
                ml: 2,
                textTransform: 'none',
                color: 'text.secondary',
                fontSize: '0.875rem',
              }}
            >
              All rules
            </Button>
          )}
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {viewMode === 'list' && (
          <>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs value={selectedTab} onChange={handleTabChange}>
                <Tab 
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      All rules
                      {chunks.length > 0 && (
                        <Chip label={chunks.length} size="small" />
                      )}
                    </Box>
                  } 
                />
                <Tab 
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      Pending
                      {pendingChunks.length > 0 && (
                        <Badge 
                          badgeContent={pendingChunks.length} 
                          color="error"
                          sx={{
                            '& .MuiBadge-badge': {
                              animation: 'pulse 2s infinite',
                              '@keyframes pulse': {
                                '0%': { opacity: 1 },
                                '50%': { opacity: 0.5 },
                                '100%': { opacity: 1 },
                              },
                            },
                          }}
                        >
                          <Box />
                        </Badge>
                      )}
                    </Box>
                  } 
                />
              </Tabs>
            </Box>

            {selectedTab === 0 && (
              <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
                Rules are how your Oppy Assistant learns your unique style. Every rule defines a pattern and a response. 
                You can review, update, or remove these rules anytime to keep Oppy aligned with your needs.
              </Typography>
            )}

            {renderListView()}
          </>
        )}
        {viewMode === 'form' && renderFormView()}
        {viewMode === 'review' && renderReviewView()}
      </DialogContent>
    </Dialog>
  );
};

export default ChunksManagementDialog; 